import isEqual from 'lodash.isequal';

import {
  cleanStateQuery,
  getCleanQuery,
  getMapQuery,
  notEmpty,
  preparePageQuery,
  prepareStateQuery
} from '@spa/shared/utils/queryParams';
import { normalizeSpecialCharacters } from '@spa/shared/utils/strings';
import { PATH_TRANSLATIONS, PATH_QUERY_MAP } from '@spa/shared/constants/url';

/**
 * Method used to update the page every time we interact
 * with our filters.
 *
 * @param history next/router
 * @param object new query state
 *
 * @returns void
 */
export const setQueryParamsFromObject = (history, object, options = { returnEntries: false, withStaticId: false }) => {
  const { query: currentQuery } = history;
  const query = Object.keys(currentQuery)
    .filter(key => object[key])
    .reduce((acc, key) => ({ ...acc, [key]: currentQuery[key] }), {});

  const mapQuery = getMapQuery(currentQuery, object);
  const cleanObject = getCleanQuery({ ...query, ...object, ...mapQuery });
  const cleanQuery = getCleanQuery(query);
  if (isEqual(cleanQuery, cleanObject)) {
    return;
  }

  const { newEntry, fullEntry } = handleQueryToPath(history, cleanObject, options.withStaticId);
  if (options.returnEntries) {
    return { newEntry, fullEntry };
  }

  history.push(fullEntry, newEntry, { shallow: true });
};

/**
 * Converts query parameters into a path format suitable for routing.
 * The function builds a path from the provided query parameters and history object,
 * and constructs new and full entries based on that path.
 *
 * @param {Object} history - The router history object, which provides access to the current path.
 * @param {Object} cleanObject - The query parameters object, containing various filtering criteria.
 *
 * @returns {Object} - An object containing:
 *   - `newEntry`: The constructed path with query parameters appended for a new entry.
 *   - `fullEntry`: The constructed path with query parameters appended for a full entry.
 */
const handleQueryToPath = (history, cleanObject, withStaticId) => {
  const [pathName] = history.asPath.split('?');
  const [path] = pathName.split('/f/');
  const { locale } = history;

  const filtersPath = generateFiltersPath(cleanObject, locale);
  const fullPath = filtersPath ? `${path}/f/${filtersPath}` : path;

  const newEntry = `${fullPath}${preparePageQuery(cleanObject, withStaticId)}`;
  const fullEntry = `${fullPath}${prepareStateQuery(cleanObject)}`;

  return { newEntry, fullEntry };
};

export const generateFiltersPath = (cleanObject, locale) =>
  Object.keys(cleanObject)
    .reduce((acc, category) => {
      const processedValues = processCategory(category, cleanObject[category], locale);

      return processedValues ? [...acc, processedValues] : acc;
    }, [])
    .join(';');

const processCategory = (category, categoryValue, locale) => {
  const valuesArray = Array.isArray(categoryValue) ? categoryValue : categoryValue ? [categoryValue] : [];

  return valuesArray
    .map(value => processValue(category, value, locale))
    .filter(Boolean)
    .join(',');
};

const processValue = (category, value, locale) => {
  const categoryMap = PATH_QUERY_MAP[category];
  if (!categoryMap) {
    return null;
  }

  const pathKey = categoryMap.pathKeys[value] ?? Object.values(categoryMap).find(v => v === value);
  if (!pathKey) {
    return null;
  }

  const translatedPath = PATH_TRANSLATIONS[locale]?.[pathKey];

  return translatedPath ? normalizeSpecialCharacters(translatedPath) : null;
};

/**
 * This methods update our router with hidden values.
 * The { shallow: true } avoids a full update on the page.
 *
 * @param history router
 * @param pathName path /accommodation/lisbon
 * @param state query params with hidden values ex sw, ne
 * @param force to force an update
 *
 * @returns void
 */
export const setHistoryPath = (history, pathName, state, force = false) => {
  const { query, asPath } = history;
  const cleanQuery = getCleanQuery(query);
  const cleanState = getCleanQuery(state);

  if (!force && isEqual(cleanState, cleanQuery)) {
    return;
  }

  const fullState = { ...cleanQuery, ...cleanState };
  const clean = cleanStateQuery(fullState);
  const hasQueryAnyValidValues = Object.keys(clean).length > 0;
  const [path] = asPath.split('?');

  if (pathName !== path) {
    window.location.href = pathName;

    return;
  }

  const queryString = hasQueryAnyValidValues ? preparePageQuery(fullState) : '';
  const fullQuery = notEmpty(fullState) ? prepareStateQuery(fullState) : '';
  const cleanPath = `${pathName}${queryString}`;
  const fullPath = force ? cleanPath : `${pathName}${fullQuery}`;

  history.push(fullPath, cleanPath, { shallow: !force });
};
